import type { CompanySettings } from '@/types/company.types';
import type { SetupProfile } from '@/types/setup-wizard.types';

/**
 * Flat mirror of the company detail fields that the company Settings tab edits
 * (`settings.contactName`, `settings.address.*`, `settings.clientInstructions`).
 * Kept flat because the wizard drives them from plain controlled inputs; mapped
 * back to the nested `settings` shape by {@link profileToDraft}.
 *
 * Credentials are deliberately excluded — they are org-admin-only and stay in the
 * Settings tab, which already gates them.
 */
export interface CompanyProfileFields {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  clientInstructions: string;
}

export const EMPTY_COMPANY_PROFILE: CompanyProfileFields = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  street: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  clientInstructions: '',
};

/** Field order drives both the form layout and the "what's missing" summary. */
export const COMPANY_PROFILE_FIELDS: Array<{
  key: keyof CompanyProfileFields;
  label: string;
}> = [
  { key: 'contactName', label: 'Contact name' },
  { key: 'contactEmail', label: 'Contact email' },
  { key: 'contactPhone', label: 'Contact phone' },
  { key: 'street', label: 'Street' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'country', label: 'Country' },
  { key: 'postalCode', label: 'Postal code' },
  { key: 'clientInstructions', label: 'Client instructions' },
];

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

export function profileFromDraft(draft: SetupProfile | undefined): CompanyProfileFields {
  if (!draft) return EMPTY_COMPANY_PROFILE;
  return {
    contactName: str(draft.contactName),
    contactEmail: str(draft.contactEmail),
    contactPhone: str(draft.contactPhone),
    street: str(draft.address?.street),
    city: str(draft.address?.city),
    state: str(draft.address?.state),
    country: str(draft.address?.country),
    postalCode: str(draft.address?.postalCode),
    clientInstructions: str(draft.clientInstructions),
  };
}

/** Hydrates the form when the wizard runs against an already-created company. */
export function profileFromSettings(settings: CompanySettings | undefined): CompanyProfileFields {
  if (!settings) return EMPTY_COMPANY_PROFILE;
  const address = settings.address ?? {};
  return {
    contactName: str(settings.contactName),
    contactEmail: str(settings.contactEmail),
    contactPhone: str(settings.contactPhone),
    street: str(address.street),
    city: str(address.city),
    state: str(address.state),
    country: str(address.country),
    postalCode: str(address.postalCode),
    clientInstructions: str(settings.clientInstructions),
  };
}

export function profileToDraft(profile: CompanyProfileFields): SetupProfile {
  return {
    contactName: profile.contactName.trim(),
    contactEmail: profile.contactEmail.trim(),
    contactPhone: profile.contactPhone.trim(),
    address: {
      street: profile.street.trim(),
      city: profile.city.trim(),
      state: profile.state.trim(),
      country: profile.country.trim(),
      postalCode: profile.postalCode.trim(),
    },
    clientInstructions: profile.clientInstructions.trim(),
  };
}

export interface CompanyProfileCompleteness {
  total: number;
  completed: number;
  /** Labels of the still-empty fields, in form order. */
  missing: string[];
  percent: number;
}

/**
 * Completeness of the optional detail fields. Drives the wizard's nudge — these
 * fields never block the step, so this is presentation only.
 */
export function getCompanyProfileCompleteness(
  profile: CompanyProfileFields
): CompanyProfileCompleteness {
  const missing = COMPANY_PROFILE_FIELDS.filter(
    (field) => profile[field.key].trim() === ''
  ).map((field) => field.label);
  const total = COMPANY_PROFILE_FIELDS.length;
  const completed = total - missing.length;
  return {
    total,
    completed,
    missing,
    percent: total === 0 ? 100 : Math.round((completed / total) * 100),
  };
}

/** Contact email is the one field with a format to get wrong; everything else is free text. */
export function getCompanyProfileError(profile: CompanyProfileFields): string | null {
  const email = profile.contactEmail.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Enter a valid contact email, or leave it blank.';
  }
  return null;
}
